import React from "react";
import RegisterPage, { STATUS_BADGES, PRIORITY_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { apiFetch } from "../lib/api";

export default function Inspections() {
  const { data, setData, siteNames } = useHSE();
  const rows = data.inspections || [];
  const legalRefIds = (data.legalCompliance || []).map((l) => l.id);
  const inspectionTypes = data.settings.inspectionTypes || [];

  const handleBeforeSave = (editingRecord) => {
    if (!editingRecord.obsRefId && (editingRecord.findings || editingRecord.correctiveAction)) {
      editingRecord.obsRefId = `ACT-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;
    }
    return null;
  };

  const handleSaveRecord = async (editingRecord) => {
    try {
      const res = await apiFetch("/inspections", {
        method: "POST",
        body: {
          ...editingRecord,
          title: editingRecord.findings || editingRecord.id || "Inspection Record",
          inspectionDate: editingRecord.date || new Date().toISOString().slice(0, 10),
          idempotencyKey: `inspection-${editingRecord.id}`,
        },
      });
      return res?.inspection ? { ...editingRecord, ...res.inspection } : editingRecord;
    } catch (err) {
      return editingRecord;
    }
  };

  const Top = (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatCard label="Total" value={rows.length} testid="insp-total" />
      <StatCard label="Closed" value={rows.filter((r) => r.status === "Closed").length} tone="safe" />
      <StatCard label="In Progress" value={rows.filter((r) => r.status === "In Progress").length} tone="warning" />
      <StatCard label="High Risk" value={rows.filter((r) => r.risk === "High" || r.risk === "Critical").length} tone="critical" />
      <StatCard label="Avg Score" value={`${Math.round(rows.reduce((a, b) => a + (b.checklistScore || 0), 0) / (rows.length || 1))}%`} tone="info" />
    </div>
  );

  return (
    <RegisterPage
      title="Inspection Register"
      subtitle="Checklist-based inspections — WAH, Fire, Lifting, LOTO, Housekeeping, MEWP & more"
      storeKey="inspections"
      idPrefix="INSP"
      topPanels={Top}
      onBeforeSave={handleBeforeSave}
      onSaveRecord={handleSaveRecord}
      columns={[
        { key: "id", label: "Insp ID" },
        { key: "date", label: "Date" },
        { key: "site", label: "Site" },
        { key: "area", label: "Area" },
        { key: "type", label: "Type" },
        { key: "inspector", label: "Inspector" },
        { key: "checklistScore", label: "Score", render: (r) => r.checklistScore ? <span className="tabular font-semibold">{r.checklistScore}%</span> : <span className="text-slate-300">—</span> },
        { key: "findings", label: "Finding" },
        { key: "risk", label: "Risk" },
        { key: "legalRef", label: "Legal Ref" },
        { key: "obsRefId", label: "Linked Action", render: (r) => r.obsRefId ? <span className="font-mono text-[11px] text-[#18B84F]">{r.obsRefId}</span> : <span className="text-slate-300">—</span> },
        { key: "responsible", label: "Responsible" },
        { key: "dueDate", label: "Due" },
        { key: "status", label: "Status" },
      ]}
      badgeMap={{ risk: PRIORITY_BADGES, status: STATUS_BADGES }}
      formFields={[
        { key: "id", label: "Inspection ID" },
        { key: "date", label: "Date", type: "date" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "area", label: "Area" },
        { key: "type", label: "Inspection Type", type: "select", options: inspectionTypes },
        { key: "inspector", label: "Inspector" },
        { key: "checklistScore", label: "Checklist Score %", type: "number" },
        { key: "findings", label: "Finding / Description", type: "textarea", full: true },
        { key: "risk", label: "Risk Rating", type: "select", options: ["Low", "Medium", "High", "Critical"] },
        { key: "legalRef", label: "Legal / Reference Standard", type: "select", options: legalRefIds },
        { key: "correctiveAction", label: "Corrective Action", type: "textarea", full: true },
        { key: "responsible", label: "Responsible Person" },
        { key: "dueDate", label: "Due Date", type: "date" },
        { key: "obsRefId", label: "Linked Action ID" },
        { key: "beforePhoto", label: "Before Photo (reference)" },
        { key: "afterPhoto", label: "After Photo (reference)" },
        { key: "completionDate", label: "Completion Date", type: "date" },
        { key: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Closed"] },
        { key: "remarks", label: "Remarks", type: "textarea", full: true },
      ]}
    />
  );
}
